import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SEROLOGY_MARKERS, SEROLOGY_QUALITATIVE } from '@toxofollow/shared';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Button, Badge, Table, Field, inputClass } from '../components/ui.js';
import { Select, FormGrid, QueryState, ErrorState } from '../components/form.js';
import { TrendChart, type TrendPoint } from '../components/charts.js';
import { SerologyImport } from '../components/SerologyImport.js';

interface SerologyRow {
  id: string;
  sampleDate: string;
  resultDate: string | null;
  marker: string;
  qualitative: string | null;
  titer: string | null;
  titerUnit: string | null;
  interpretation: string | null;
  signedAt: string | null;
}

const MARKER_COLORS: Record<string, string> = {
  igg: '#2563eb',
  igm: '#dc2626',
  iga: '#7c3aed',
  pcr: '#0891b2',
};

function qualTone(q: string | null): 'ok' | 'danger' | 'warn' | 'info' {
  if (q === 'positive') return 'danger';
  if (q === 'negative') return 'ok';
  if (q === 'equivocal') return 'warn';
  return 'info';
}

export function Serology() {
  const { t, locale } = useI18n();
  const lang = locale === 'en' ? 'en' : 'fr';
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const q = useQuery({ queryKey: ['serology'], queryFn: () => api.get<{ serology: SerologyRow[] }>('/labs/serology') });

  const rows = q.data?.serology ?? [];
  const byMarker: Record<string, SerologyRow[]> = {};
  for (const r of rows) (byMarker[r.marker] ??= []).push(r);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-800">{t('exam.serology')}</h1>
        <Button onClick={() => setShowForm((s) => !s)}>{showForm ? t('common.cancel') : `+ ${t('exam.serology')}`}</Button>
      </div>

      {showForm && <SerologyForm onDone={() => setShowForm(false)} />}

      {/* --- Import a serology report (AI-assisted) --- */}
      <Card title={`${t('exam.serology')} — ${lang === 'en' ? 'Import a report' : 'Importer un compte rendu'}`}>
        <SerologyImport
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ['serology'] });
            void qc.invalidateQueries({ queryKey: ['alerts'] });
          }}
        />
      </Card>

      <Card title={t('exam.serology')}>
        <QueryState
          isLoading={q.isLoading}
          isError={q.isError}
          error={q.error}
          errorFallback={t('common.error')}
          isEmpty={rows.length === 0}
          emptyMessage="—"
        >
          {/* Quantitative titer trends per marker */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {SEROLOGY_MARKERS.map((marker) => {
              const series = byMarker[marker] ?? [];
              const titered = series.filter((r) => r.titer != null && r.titer !== '');
              if (titered.length < 2) return null;
              const points: TrendPoint[] = titered.map((r) => ({ date: r.sampleDate, value: Number(r.titer) }));
              return (
                <div key={marker} className="border border-slate-200 rounded-lg p-3">
                  <h3 className="font-medium text-slate-700 mb-2">{t(`serology.${marker}`)}</h3>
                  <TrendChart data={points} unit={titered[0]?.titerUnit ?? undefined} color={MARKER_COLORS[marker]} />
                </div>
              );
            })}
          </div>

          <Table headers={[t('lab.sampleDate'), t('sero.marker'), t('sero.qual'), t('sero.titer'), t('interp.title')]}>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="py-2 pr-4">
                  {r.sampleDate}
                  {r.resultDate && <span className="text-slate-400 text-xs"> → {r.resultDate}</span>}
                </td>
                <td className="py-2 pr-4 font-medium">{t(`serology.${r.marker}`)}</td>
                <td className="py-2 pr-4">{r.qualitative ? <Badge tone={qualTone(r.qualitative)}>{r.qualitative}</Badge> : '—'}</td>
                <td className="py-2 pr-4">{r.titer ? `${r.titer}${r.titerUnit ? ` ${r.titerUnit}` : ''}` : '—'}</td>
                <td className="py-2 pr-4 text-slate-500">{r.interpretation || '—'}</td>
              </tr>
            ))}
          </Table>
        </QueryState>
      </Card>
    </div>
  );
}

function SerologyForm({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [sampleDate, setSampleDate] = useState(today);
  const [resultDate, setResultDate] = useState('');
  const [marker, setMarker] = useState<string>('igg');
  const [qualitative, setQualitative] = useState('');
  const [titer, setTiter] = useState('');
  const [titerUnit, setTiterUnit] = useState('');
  const [interpretation, setInterpretation] = useState('');

  const m = useMutation({
    mutationFn: () =>
      api.post('/labs/serology', {
        sampleDate,
        resultDate: resultDate || null,
        marker,
        qualitative: qualitative || undefined,
        titer: titer.trim() || undefined,
        titerUnit: titerUnit.trim() || undefined,
        interpretation: interpretation.trim() || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['serology'] });
      onDone();
    },
  });

  return (
    <Card title={`+ ${t('exam.serology')}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <FormGrid>
          <Field label={t('lab.sampleDate')}>
            <input className={inputClass} type="date" value={sampleDate} onChange={(e) => setSampleDate(e.target.value)} required />
          </Field>
          <Field label={t('lab.resultDate')}>
            <input className={inputClass} type="date" value={resultDate} onChange={(e) => setResultDate(e.target.value)} />
          </Field>
          <Field label={t('sero.marker')}>
            <Select value={marker} onChange={(e) => setMarker(e.target.value)}>
              {SEROLOGY_MARKERS.map((mk) => (
                <option key={mk} value={mk}>{t(`serology.${mk}`)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('sero.qualitative')}>
            <Select value={qualitative} onChange={(e) => setQualitative(e.target.value)}>
              <option value="">—</option>
              {SEROLOGY_QUALITATIVE.map((qv) => (
                <option key={qv} value={qv}>{qv}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('sero.titer')}>
            <input className={inputClass} inputMode="decimal" value={titer} onChange={(e) => setTiter(e.target.value)} />
          </Field>
          <Field label={t('sero.titerUnit')}>
            <input className={inputClass} value={titerUnit} onChange={(e) => setTiterUnit(e.target.value)} placeholder="IU/mL" />
          </Field>
        </FormGrid>
        <Field label={t('interp.title')}>
          <input className={inputClass} value={interpretation} onChange={(e) => setInterpretation(e.target.value)} />
        </Field>
        {m.isError && <div className="mb-3"><ErrorState error={m.error} fallback={t('common.error')} /></div>}
        <div className="flex gap-2">
          <Button type="submit" disabled={m.isPending}>{t('common.save')}</Button>
          <Button type="button" variant="secondary" onClick={onDone}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}
